<ngx-json-ld [json]="schema"></ngx-json-ld>
<app-header #header></app-header>
<app-navigation-bar (onBurgerClicked)="toggleDrawer()"></app-navigation-bar>
<app-drawer [open]="drawerIsOpen" (onClose)="drawerIsOpen=false"></app-drawer>

<div class="page-content">
  <main class="ai-content">
    <section class="ai-hero">
      <p class="eyebrow">AI and LLM access</p>
      <h1>CheapCharts for AI Agents</h1>
      <p class="lead">
        CheapCharts tracks digital movie and TV prices, title search, deals, charts, recommendations, and top sellers across Apple TV / iTunes, Amazon Prime Video, Vudu, and Google Play. AI agents should use the GPT API instead of scraping website pages.
      </p>
      <div class="link-row">
        <a class="primary-link" href="/llms.txt">Open llms.txt</a>
        <a class="secondary-link" href="https://buster.cheapcharts.de/v1/gptapi/Search.php?action=search&amp;store=itunes&amp;country=us&amp;itemType=all&amp;query=Fight%20Club&amp;limit=5">Search API example</a>
      </div>
    </section>

    <section>
      <h2>API discovery</h2>
      <p>
        Base URL: <code>{{apiBaseUrl}}</code>
      </p>
      <p>
        Full documentation: <a href="/llms.txt">https://www.cheapcharts.com/llms.txt</a>
      </p>
      <p>
        Responses are structured JSON and include CheapCharts product links where available. If you only know a title and not an IMDb ID, start with <code>Search.php</code>, then use the matching result for price, deal, or product-page follow-up.
      </p>
      <p>
        Key CheapCharts pages also expose JSON-LD <code>potentialAction</code> hints for the GPT API, including search, deal lists, and movie detail pages.
      </p>
    </section>

    <section>
      <h2>Core examples</h2>
      <div class="example-list">
        <article>
          <h3>Search</h3>
          <p>Use title search first when the user names a movie or show but you do not yet know the matching CheapCharts item or IMDb ID.</p>
          <pre><code>GET https://buster.cheapcharts.de/v1/gptapi/Search.php?action=search&amp;store=itunes&amp;country=us&amp;itemType=all&amp;query=Fight%20Club&amp;limit=5</code></pre>
        </article>

        <article>
          <h3>Deals</h3>
          <p>Use current deal data when a user asks for cheap movies, price drops, discounts, or titles below a budget.</p>
          <pre><code>GET https://buster.cheapcharts.de/v1/gptapi/Deals.php?action=getDeals&amp;store=itunes&amp;country=us&amp;itemType=buymovies&amp;genre=ActionAdventure&amp;quality=hd4k&amp;sort=greatestSavings&amp;maxPrice=4.99&amp;limit=20</code></pre>
        </article>

        <article>
          <h3>Prices</h3>
          <p>Use price lookup when the user asks about a specific movie and you know the IMDb ID.</p>
          <pre><code>GET https://buster.cheapcharts.de/v1/gptapi/Prices.php?action=getPrices&amp;store=itunes&amp;country=us&amp;itemType=buymovies&amp;imdbIDs=tt0468569,tt2911666</code></pre>
        </article>

        <article>
          <h3>Recommendations</h3>
          <p>Use recommendations for discovery questions such as genre picks, highly rated films, or what to watch next.</p>
          <pre><code>GET https://buster.cheapcharts.de/v1/gptapi/Recommendations.php?action=getRecommendations&amp;store=amazon&amp;country=us&amp;itemType=buymovies&amp;genre=SciFiFantasy&amp;quality=hd4k&amp;limit=15&amp;imdbRating=7</code></pre>
        </article>

        <article>
          <h3>Topseller</h3>
          <p>Use top sellers when the user asks what is popular, trending, or currently selling across stores.</p>
          <pre><code>GET https://buster.cheapcharts.de/v1/gptapi/Topseller.php?action=getTopsellerForStartpage&amp;country=us&amp;store=itunes,amazon,vudu,googlePlay&amp;maxItemCount=5</code></pre>
        </article>
      </div>
    </section>

    <section>
      <h2>Questions agents can answer</h2>
      <ul>
        <li>What are the best 4K movie deals under $5 in the US Apple TV store?</li>
        <li>Is The Dark Knight currently cheaper on Apple TV, and should I wait for a sale?</li>
        <li>Which highly rated sci-fi movies are discounted on Prime Video?</li>
        <li>What movies and TV seasons are top sellers today across US stores?</li>
      </ul>
    </section>

    <section>
      <h2>Country and store defaults</h2>
      <p>
        Always set <code>country</code> from the user's location or explicit request. If no country is known, default to <code>us</code>. Always set <code>store</code> from the user's preferred storefront. If no store is known, use <code>itunes</code> for broad country coverage, or use Topseller with multiple stores where the country supports them.
      </p>
      <p>
        Common store values are <code>itunes</code>, <code>amazon</code>, <code>vudu</code>, and <code>googlePlay</code>. Common item types are <code>buymovies</code> for movies and <code>seasons</code> for TV seasons.
      </p>
    </section>
  </main>
</div>
